/**
 * 🏊🚣 Which pace target a phase prescribes, in which unit — the FE half of
 * the per-100 m / per-500 m prescription slots (D6, triathlon finish
 * 2026-09-21).
 *
 * THE DEFECT. The planner writes a swim's targets into
 * `target_pace_min/max_per_100m` (and a row's into `..._per_500m`) — the
 * schema's own words: "SWIMMING ONLY (prescription_axis='pace_per_100m')" —
 * and the extractor strips the per-km slots for those sports. Every phone
 * surface read the per-km slots only. So a swim's prescribed pace reached the
 * watch and was invisible on the phone: the targets panel returned null, the
 * phase list showed no pace, and the header pill showed none.
 *
 * THE RULE. A phase's target comes from ONE slot family:
 *   - a declared `prescription_axis` of `pace_per_100m` / `pace_per_500m`
 *     picks that family, when it carries a value;
 *   - otherwise the first populated family, in the order per_km, per_100m,
 *     per_500m. per_km first means a phase that carries the per-km slots is
 *     read EXACTLY as before. The one exception: a per-km slot holding only
 *     a placeholder ('0:00', free text) does not outrank a REAL per-100/500
 *     target on the same phase. Without such a target it still wins, so
 *     every phase that lacks the new slots is unchanged.
 *
 * TWO DIFFERENT NOTIONS OF "POPULATED", ON PURPOSE.
 *   - per_km keeps the legacy truthiness test and the raw stored value. Run
 *     and bike cards must render byte-for-byte what they rendered before this
 *     helper existed (owner ruling 2026-09-21), and that includes whatever
 *     legacy rows carry.
 *   - per_100m / per_500m are new on every surface, so they get the house
 *     standard from day one: a value is only a target if it is a real M:SS
 *     pace. '0:00', '', a number or free text is null — and a null is silence,
 *     never a placeholder.
 *
 * Each consumer keeps its OWN legacy string format (PhaseRow en-dash
 * unitless, targets panel hyphen + '/km', header pill en-dash + '/km', the
 * timer's Target chip lo-only and unsuffixed, its voice cue '… per
 * kilometer'). The per-consumer formatters below reproduce those exactly for
 * per-km phases — the legacy expression is the literal fallback — and only
 * phases whose target resolves to per-100 m / per-500 m take the new path.
 *
 * The two timer formatters (`phaseTargetChip`, `phaseTargetSpoken`) are the
 * seam FE-3 wires into UnifiedWorkoutTimer; this PR (FE-2, the detail
 * screen) supplies and pins them but does not touch the timer.
 */
import { rateAxisUnit } from './cardioUnits';

export type PhasePaceUnit = 'per_km' | 'per_100m' | 'per_500m';

/** The slots this module reads. Every field is optional and may be junk. */
export interface PhasePaceSlots {
  prescription_axis?: unknown;
  target_pace?: unknown;
  target_pace_min_per_km?: unknown;
  target_pace_max_per_km?: unknown;
  target_pace_min_per_100m?: unknown;
  target_pace_max_per_100m?: unknown;
  target_pace_min_per_500m?: unknown;
  target_pace_max_per_500m?: unknown;
}

export interface PhasePaceTarget {
  /** Fast bound as stored (`target_pace_min_*`), or null. */
  lo: unknown;
  /** Slow bound as stored (`target_pace_max_*`), or null. */
  hi: unknown;
  unit: PhasePaceUnit;
  /** '/km', '/100m' or '/500m' — from cardioUnits, never retyped here. */
  suffix: string;
}

const UNIT_RATE: Record<PhasePaceUnit, 'pace_per_km' | 'pace_per_100m' | 'pace_per_500m'> = {
  per_km: 'pace_per_km',
  per_100m: 'pace_per_100m',
  per_500m: 'pace_per_500m',
};

function suffixFor(unit: PhasePaceUnit): string {
  return rateAxisUnit(UNIT_RATE[unit]) ?? '';
}

const MSS = /^(\d{1,3}):([0-5]\d)$/;

/**
 * A real M:SS pace, trimmed, or null. '0:00' is the zero-valued placeholder
 * the schema forbids ("never a zero-valued placeholder string") and the
 * sentinel repair nulls at write time; a legacy row that slipped past it must
 * not render as a target.
 */
export function cleanPaceString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  const m = MSS.exec(s);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]) > 0 ? s : null;
}

function newFamily(
  phase: PhasePaceSlots,
  unit: 'per_100m' | 'per_500m',
): PhasePaceTarget | null {
  const lo = cleanPaceString(
    unit === 'per_100m' ? phase.target_pace_min_per_100m : phase.target_pace_min_per_500m,
  );
  const hi = cleanPaceString(
    unit === 'per_100m' ? phase.target_pace_max_per_100m : phase.target_pace_max_per_500m,
  );
  if (!lo && !hi) return null;
  return { lo, hi, unit, suffix: suffixFor(unit) };
}

function perKmFamily(phase: PhasePaceSlots): PhasePaceTarget | null {
  // Legacy truthiness, raw values — see the module docstring.
  const lo = phase.target_pace_min_per_km;
  const hi = phase.target_pace_max_per_km;
  if (!lo && !hi) return null;
  return { lo: lo || null, hi: hi || null, unit: 'per_km', suffix: suffixFor('per_km') };
}

/** The phase's pace target, or null when it prescribes none. Never throws. */
export function phasePaceTarget(phase: unknown): PhasePaceTarget | null {
  if (!phase || typeof phase !== 'object') return null;
  const p = phase as PhasePaceSlots;
  const axis = typeof p.prescription_axis === 'string' ? p.prescription_axis : '';

  if (axis === 'pace_per_100m') {
    const declared = newFamily(p, 'per_100m');
    if (declared) return declared;
  } else if (axis === 'pace_per_500m') {
    const declared = newFamily(p, 'per_500m');
    if (declared) return declared;
  }
  const km = perKmFamily(p);
  // A per-km slot holding a real pace wins, as it always did. A per-km slot
  // holding only a placeholder ('0:00', free text) does NOT outrank a real
  // per-100/500 target — but it still renders exactly as before whenever no
  // such target exists, so every phase without the new slots is unchanged.
  const kmReal =
    km && (cleanPaceString(p.target_pace_min_per_km) || cleanPaceString(p.target_pace_max_per_km))
      ? km
      : null;
  return kmReal ?? newFamily(p, 'per_100m') ?? newFamily(p, 'per_500m') ?? km;
}

/** The target, only when it is a per-100 m / per-500 m one. */
function nonKmTarget(phase: unknown): PhasePaceTarget | null {
  const t = phasePaceTarget(phase);
  return t && t.unit !== 'per_km' ? t : null;
}

function joinRange(t: PhasePaceTarget, dash: string): string {
  if (t.lo && t.hi) return `${t.lo}${dash}${t.hi}${t.suffix}`;
  return `${t.lo || t.hi}${t.suffix}`;
}

/** True when the phase carries any pace target (the targets-panel filter). */
export function phaseHasPaceTarget(phase: unknown): boolean {
  return phasePaceTarget(phase) !== null;
}

/**
 * WorkoutDetail `PhaseRow` — the range WITHOUT `target_pace` (the caller keeps
 * `phase.target_pace || …` in front, as it always has).
 *
 * per-km (legacy, byte-for-byte): `min–max` en-dash and unitless, else `min`,
 * else null. A lone max has always rendered nothing here.
 * per-100 m / per-500 m: `lo–hi/100m`, or the one bound present plus suffix.
 */
export function phaseRowPaceRange(phase: unknown): string | null {
  const t = nonKmTarget(phase);
  if (t) return joinRange(t, '–');
  const p = (phase && typeof phase === 'object' ? phase : {}) as PhasePaceSlots;
  return (p.target_pace_min_per_km && p.target_pace_max_per_km
    ? `${p.target_pace_min_per_km}–${p.target_pace_max_per_km}`
    : (p.target_pace_min_per_km as string) || null);
}

/**
 * `HeartRateZonesDisplayPre` targets panel (pace + hybrid branches).
 *
 * per-km (legacy, byte-for-byte): `min-max/km` hyphen, else the lone bound
 * unsuffixed, else 'open pace'.
 * per-100 m / per-500 m: `lo-hi/100m`, or the one bound present plus suffix.
 */
export function targetsPanelPaceLabel(phase: unknown): string {
  const t = nonKmTarget(phase);
  if (t) return joinRange(t, '-');
  const p = (phase && typeof phase === 'object' ? phase : {}) as PhasePaceSlots;
  return (p.target_pace_min_per_km && p.target_pace_max_per_km
    ? `${p.target_pace_min_per_km}-${p.target_pace_max_per_km}/km`
    : (p.target_pace_min_per_km as string) || (p.target_pace_max_per_km as string) || 'open pace');
}

/**
 * `PrimaryMetricsGrid` header pill — the phase-derived pace, before the pill
 * appends its suffix.
 *
 * per-km (legacy, byte-for-byte): `min–max` en-dash unitless, else the
 * phase's `target_pace`, else null.
 * per-100 m / per-500 m: `lo–hi/100m`, already suffixed — `suffixPillPace`
 * then leaves it alone.
 */
export function metricsPillPhasePace(phase: unknown): string | null {
  if (!phase || typeof phase !== 'object') return null;
  const t = nonKmTarget(phase);
  if (t) return joinRange(t, '–');
  const p = phase as PhasePaceSlots;
  if (p.target_pace_min_per_km && p.target_pace_max_per_km) {
    return `${p.target_pace_min_per_km}–${p.target_pace_max_per_km}`;
  }
  if (p.target_pace) return p.target_pace as string;
  return null;
}

const RATE_SUFFIXES_PRESENT = ['/km', '/100m', '/500m'];

/**
 * The pill's suffix step. Legacy: a string already containing '/km' is kept,
 * anything else gets '/km' appended. A string already carrying '/100m' or
 * '/500m' is kept too — before this it became '1:52–1:58/100m/km'.
 */
export function suffixPillPace(value: unknown): string {
  if (typeof value === 'string' && RATE_SUFFIXES_PRESENT.some((s) => value.includes(s))) {
    return value;
  }
  return `${value}/km`;
}

const SPOKEN_UNIT: Record<PhasePaceUnit, string> = {
  per_km: 'per kilometer',
  per_100m: 'per 100 metres',
  per_500m: 'per 500 metres',
};

/**
 * `UnifiedWorkoutTimer` Target chip — the value under "Target" (FE-3 seam).
 *
 * per-km (legacy, byte-for-byte): `target_pace_min_per_km || target_pace`,
 * lo-only and unsuffixed, else null (the chip hides).
 * per-100 m / per-500 m: the fast bound (or the one bound present) plus its
 * unit — '1:52/100m'. A lo-only chip without a unit would read as a per-km
 * pace, so the swim/row path always carries its suffix.
 */
export function phaseTargetChip(phase: unknown): string | null {
  if (!phase || typeof phase !== 'object') return null;
  const t = nonKmTarget(phase);
  if (t) return `${t.lo || t.hi}${t.suffix}`;
  const p = phase as PhasePaceSlots;
  return ((p.target_pace_min_per_km || p.target_pace) as string) || null;
}

/**
 * The timer's Target chip for a phase aimed at an effort, not a pace:
 * `'Effort 8/10'` from `rpe_target` (1–10), the wording the HYROX course table
 * and the step's own instructions already use. Null when the phase declares
 * another axis (pace, heart rate, power: that axis is its target) or carries
 * no usable effort. The timer shows it only where `phaseTargetChip` is null,
 * so a paced phase never changes. Before this, an effort-led phase showed no
 * target at all (endurance PR-8f part 2; every RPE-axis phase, not only HYROX).
 */
export function phaseEffortChip(phase: unknown): string | null {
  if (!phase || typeof phase !== 'object') return null;
  const p = phase as { prescription_axis?: unknown; rpe_target?: unknown };
  if (typeof p.prescription_axis === 'string' && p.prescription_axis !== '' && p.prescription_axis !== 'rpe') return null;
  const rpe = typeof p.rpe_target === 'string' ? Number(p.rpe_target) : p.rpe_target;
  if (typeof rpe !== 'number' || !Number.isFinite(rpe) || rpe < 1 || rpe > 10) return null;
  return `Effort ${Number(rpe.toFixed(1))}/10`;
}

/**
 * `UnifiedWorkoutTimer` phase-start voice cue — the clause after " at "
 * (FE-3 seam). The caller keeps its own " at " wrapper, i.e.
 * `if (spoken) msg += ' at ' + spoken`.
 *
 * per-km (legacy, byte-for-byte): `${target_pace_min_per_km || target_pace}
 * per kilometer`, else null (nothing is said).
 * per-100 m / per-500 m: '1:52 per 100 metres' / '2:05 per 500 metres'.
 */
export function phaseTargetSpoken(phase: unknown): string | null {
  if (!phase || typeof phase !== 'object') return null;
  const t = nonKmTarget(phase);
  if (t) return `${t.lo || t.hi} ${SPOKEN_UNIT[t.unit]}`;
  const p = phase as PhasePaceSlots;
  const legacy = p.target_pace_min_per_km || p.target_pace;
  return legacy ? `${legacy} ${SPOKEN_UNIT.per_km}` : null;
}

// ═══════════════════════════════════════════════════════════════════════
// UNITS-U5A (owner ruling #42): per-km targets for a MILES athlete.
// Opt-in only (`{ miles: true }`, which callers set when the server opted the
// athlete in). Without it every formatter above returns its legacy string.
// The prescription slots stay per-km on the wire; only the display converts.
// Per-100 m / per-500 m targets never convert (the pool / the erg decides).
// ═══════════════════════════════════════════════════════════════════════

const KM_PER_MI = 1.609344;

/** "5:30" per km -> "8:51" per mile; null for anything that is not M:SS. */
export function perKmToPerMile(value: unknown): string | null {
  const clean = cleanPaceString(value);
  if (!clean) return null;
  const m = /^(\d+):(\d{2})$/.exec(clean);
  if (!m) return null;
  const total = Math.round((Number(m[1]) * 60 + Number(m[2])) * KM_PER_MI);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Convert every M:SS in a per-km range string ("5:30–5:50") to per mile. */
function perKmTextToPerMile(text: string): string {
  return text.replace(/\d+:\d{2}/g, (mmss) => perKmToPerMile(mmss) ?? mmss);
}

export interface PaceDisplayOpts { miles?: boolean }

/** `phaseTargetChip`, in the athlete's unit: a per-km target reads "8:51/mi". */
export function phaseTargetChipFor(phase: unknown, opts: PaceDisplayOpts = {}): string | null {
  const legacy = phaseTargetChip(phase);
  if (!opts.miles || legacy == null || nonKmTarget(phase)) return legacy;
  return `${perKmTextToPerMile(legacy)}/mi`;
}

/** `phaseTargetSpoken`, in the athlete's unit: "8:51 per mile". */
export function phaseTargetSpokenFor(phase: unknown, opts: PaceDisplayOpts = {}): string | null {
  const legacy = phaseTargetSpoken(phase);
  if (!opts.miles || legacy == null || nonKmTarget(phase)) return legacy;
  return legacy
    .replace(/ per kilometer$/, '')
    .replace(/\d+:\d{2}/g, (mmss) => perKmToPerMile(mmss) ?? mmss) + ' per mile';
}

/** `targetsPanelPaceLabel`, in the athlete's unit ("8:51-9:23/mi"). */
export function targetsPanelPaceLabelFor(phase: unknown, opts: PaceDisplayOpts = {}): string {
  const legacy = targetsPanelPaceLabel(phase);
  if (!opts.miles || nonKmTarget(phase) || !/\d+:\d{2}/.test(legacy)) return legacy;
  return perKmTextToPerMile(legacy).replace(/\/km$/, '') + '/mi';
}

/** `phaseRowPaceRange`, in the athlete's unit (unitless, like the legacy row). */
export function phaseRowPaceRangeFor(phase: unknown, opts: PaceDisplayOpts = {}): string | null {
  const legacy = phaseRowPaceRange(phase);
  if (!opts.miles || legacy == null || nonKmTarget(phase)) return legacy;
  return perKmTextToPerMile(legacy);
}

/** The header pill (`metricsPillPhasePace` + `suffixPillPace`) in the athlete's unit. */
export function pillPaceFor(value: unknown, opts: PaceDisplayOpts = {}): string {
  const legacy = suffixPillPace(value);
  if (!opts.miles || typeof value !== 'string' || /\/(100m|500m)/.test(legacy)) return legacy;
  return perKmTextToPerMile(legacy.replace(/\/km$/, '')) + '/mi';
}

/**
 * Any per-km pace text ("5:30", "5:30–5:50/km") in the athlete's unit:
 * every M:SS converted and every "/km" swapped for "/mi" when
 * `opts.miles`; otherwise the text exactly as given.
 */
export function perKmPaceTextFor(text: string, opts: PaceDisplayOpts = {}): string {
  if (!opts.miles || !text || !/\d+:\d{2}/.test(text)) return text;
  const converted = perKmTextToPerMile(text);
  return converted.replace(/\/km\b/g, '/mi');
}

